/**
 * @file DSAProblems.jsx
 * @description The core practice arena for data structures and algorithms. 
 *              Displays a Bento-grid metrics header, a sticky command bar with 
 *              Search, Topic Ribbon, and Difficulty filters, followed by a sleek 
 *              headerless problem list.
 * @module components/DSAProblems
 * @requires useAuth - Authentication context for user-specific metrics.
 * @requires Navbar - Global navigation.
 * @requires Footer - Global footer.
 */
import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { apiGet, publicApiGet, apiGetCached, publicApiGetCached } from '../utils/apiUtils';
import Navbar from './layout/Navbar';
import Footer from './layout/Footer';
import {
    FiCode, FiCheckCircle, FiSearch, FiArrowRight, FiAlertCircle,
    FiRefreshCw, FiChevronLeft, FiChevronRight, FiChevronsRight, FiChevronsLeft, FiCalendar, FiTarget,
    FiTag, FiChevronDown, FiActivity, FiCheck, FiTerminal, FiCircle
} from 'react-icons/fi';
import { FaFire, FaBullseye } from 'react-icons/fa';

/**
 * @component DSAProblems
 * @description Main functional component for the DSA Practice Arena.
 */
const DSAProblems = ({ navigate }) => {
    // ── 1. ACTIVE FILTERS & PAGINATION STATE ──
    // ── State ──
    const [selectedTopic, setSelectedTopic] = useState('all');
    const [selectedDifficulty, setSelectedDifficulty] = useState('all');
    const [selectedCompanies, setSelectedCompanies] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const questionsPerPage = 15; // Increased slightly for full-width layout

    // Data states
    const [questions, setQuestions] = useState([]);
    const [topics, setTopics] = useState([]);
    const [companyTags, setCompanyTags] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // ── 2. AUTH & USER METRICS ──
    const { isAuthenticated, user } = useAuth();

    // Set browser tab title on mount
    useEffect(() => { document.title = 'SimpleCode | DSA Practice Arena'; }, []);

    const [dailyProblem, setDailyProblem] = useState(null);
    const [dailyStreak, setDailyStreak] = useState({ currentStreak: 0, solvedToday: false });
    const [submissionStreak, setSubmissionStreak] = useState({ currentStreak: 0, longestStreak: 0 });

    // ── Navigation Links (Conditional) ──
    const navLinks = [
        { id: "home", label: "Home", action: () => navigate("/") },
        { id: "learning", label: "Learning", action: () => navigate("/learning") },
        { id: "dashboard", label: "Dashboard", action: () => navigate("/dashboard"), show: isAuthenticated },
        { id: "dsa", label: "DSA Problems", action: () => navigate("/dsa-problems") },
        { id: "system-design", label: "System Design", action: () => navigate("/system-design") }
    ].filter(link => link.show !== false);

    const footerLinks = [
        { label: "Home", action: () => navigate("/") },
        { label: "DSA Problems", action: () => navigate("/dsa-problems") },
        { label: "System Design", action: () => navigate("/system-design") }
    ];

    // Scrolling logic
    const topicsScrollRef = useRef(null);
    const [canScrollLeft, setCanScrollLeft] = useState(false);
    const [canScrollRight, setCanScrollRight] = useState(true);

    const companiesScrollRef = useRef(null);
    const [canScrollLeftCompanies, setCanScrollLeftCompanies] = useState(false);
    const [canScrollRightCompanies, setCanScrollRightCompanies] = useState(true);

    const handleScroll = () => {
        if (topicsScrollRef.current) {
            const { scrollLeft, scrollWidth, clientWidth } = topicsScrollRef.current;
            setCanScrollLeft(scrollLeft > 0);
            setCanScrollRight(scrollLeft + clientWidth < scrollWidth - 1);
        }
    };

    const handleCompaniesScroll = () => {
        if (companiesScrollRef.current) {
            const { scrollLeft, scrollWidth, clientWidth } = companiesScrollRef.current;
            setCanScrollLeftCompanies(scrollLeft > 0);
            setCanScrollRightCompanies(scrollLeft + clientWidth < scrollWidth - 1);
        }
    };

    useEffect(() => {
        handleScroll();
        handleCompaniesScroll();
    }, [topics, companyTags]);

    const scrollTopicsRight = () => {
        if (topicsScrollRef.current) {
            topicsScrollRef.current.scrollBy({ left: 250, behavior: 'smooth' });
        }
    };

    const scrollTopicsLeft = () => {
        if (topicsScrollRef.current) {
            topicsScrollRef.current.scrollBy({ left: -250, behavior: 'smooth' });
        }
    };

    const scrollCompaniesRight = () => {
        if (companiesScrollRef.current) {
            companiesScrollRef.current.scrollBy({ left: 250, behavior: 'smooth' });
        }
    };

    const scrollCompaniesLeft = () => {
        if (companiesScrollRef.current) {
            companiesScrollRef.current.scrollBy({ left: -250, behavior: 'smooth' });
        }
    };

    /* ══════════════════════════════════════════════════════════════
     * DATA FETCHING & LOGIC
     * ══════════════════════════════════════════════════════════════ */

    /** Smoothly scrolls the window back to the top (used after pagination changes). */
    const scrollToTop = () => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    /** Toggles a company tag filter on/off in the active filter set. */
    const toggleCompany = (company) => {
        if (selectedCompanies.includes(company)) {
            setSelectedCompanies(selectedCompanies.filter(c => c !== company));
        } else {
            setSelectedCompanies([...selectedCompanies, company]);
        }
    };

    /** Fetches today's daily challenge problem and the user's daily/submission streaks. */
    const fetchDailyData = async (currentUser) => {
        try {
            // Cache today's problem for 5 minutes - Use public fetcher since this is a public endpoint
            const problemResponse = await publicApiGetCached('/daily-problem/today', 300000);
            if (problemResponse.ok) {
                setDailyProblem(await problemResponse.json());
            }
            if (isAuthenticated && currentUser?.id) {
                // Cache streaks for 5 minutes
                const [streakResponse, subStreakResponse] = await Promise.all([
                    apiGetCached(`/daily-problem/streak/${currentUser.id}`, 300000),
                    apiGetCached(`/auth/submission-streak/${currentUser.id}`, 300000)
                ]);

                if (streakResponse.ok) {
                    setDailyStreak(await streakResponse.json());
                }
                if (subStreakResponse.ok) {
                    setSubmissionStreak(await subStreakResponse.json());
                }
            }
        } catch (err) {
            console.error('Error fetching daily data:', err);
        }
    };

    /** Fetches the full question bank, computes topic/company tag aggregates, and sorts by priority. */
    const fetchQuestions = async () => {
        try {
            setLoading(true);
            setError(null);

            // Cache question list for 5 minutes
            const endpoint = isAuthenticated ? '/questions/' : '/questions/public';
            const response = isAuthenticated 
                ? await apiGetCached(endpoint, 300000)
                : await publicApiGetCached(endpoint, 300000);

            if (!response.ok) {
                throw new Error('Failed to fetch questions');
            }

            const data = await response.json();
            const sortedQuestions = data.sort((a, b) => {
                if (a.priority_order !== b.priority_order) {
                    return (a.priority_order || 999) - (b.priority_order || 999);
                }
                return a.id - b.id;
            });
            setQuestions(sortedQuestions);

            // Compute topics
            const uniqueTopics = [...new Set(sortedQuestions.map(q => q.topic).filter(Boolean))];
            const topicsWithCounts = uniqueTopics.map(topic => {
                const topicQuestions = sortedQuestions.filter(q => q.topic === topic);
                const totalQuestions = topicQuestions.length;
                const solvedQuestions = topicQuestions.filter(q => q.is_solved).length;
                return {
                    id: topic.toLowerCase().replace(/\s+/g, '-'),
                    name: topic,
                    count: totalQuestions,
                    solved: solvedQuestions,
                    isCompleted: totalQuestions > 0 && solvedQuestions === totalQuestions,
                    priority_order: topicQuestions[0]?.priority_order || 999
                };
            }).sort((a, b) => a.priority_order - b.priority_order);

            setTopics(topicsWithCounts);

            // Compute company tags
            const allCompanies = sortedQuestions
                .map(q => q.company_tag)
                .filter(Boolean)
                .flatMap(tag => tag.split(',').map(t => t.trim()))
                .filter(Boolean);

            const uniqueCompanies = [...new Set(allCompanies)].sort();
            setCompanyTags(uniqueCompanies);

            setSelectedTopic('all');
        } catch (err) {
            console.error('Error fetching questions:', err);
            setError('Failed to load questions. Please check if the backend server is running.');
        } finally {
            setLoading(false);
        }
    };

    /** Capitalizes only the first letter of a string (used for display formatting). */
    const capitalizeFirstLetter = (str) => {
        if (!str) return 'Unknown';
        return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
    };

    // Initialization
    useEffect(() => {
        if (isAuthenticated && user?.id) {
            fetchDailyData(user);
        } else {
            fetchDailyData(null);
        }
        fetchQuestions();
    }, [isAuthenticated, user]);

    // Reset pagination when filters change
    useEffect(() => {
        setCurrentPage(1);
    }, [selectedTopic, selectedDifficulty, searchQuery, selectedCompanies]);

    /** Applies all active filters (topic, difficulty, company, search) to the question bank. */
    const getFilteredProblems = () => {
        if (!questions.length) return [];
        let filteredProblems = questions;

        if (selectedTopic !== 'all') {
            const currentTopicName = topics.find(t => t.id === selectedTopic)?.name;
            filteredProblems = filteredProblems.filter(q => q.topic === currentTopicName);
        }
        if (selectedDifficulty !== 'all') {
            filteredProblems = filteredProblems.filter(p => p.difficulty?.toLowerCase() === selectedDifficulty);
        }
        if (selectedCompanies.length > 0) {
            filteredProblems = filteredProblems.filter(p => {
                if (!p.company_tag) return false;
                const questionCompanies = p.company_tag.split(',').map(t => t.trim());
                return selectedCompanies.some(selectedCompany => questionCompanies.includes(selectedCompany));
            });
        }
        if (searchQuery) {
            filteredProblems = filteredProblems.filter(p =>
                p.id.toString().includes(searchQuery.toLowerCase()) ||
                p.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                (p.subtopics && p.subtopics.toLowerCase().includes(searchQuery.toLowerCase()))
            );
        }
        return filteredProblems;
    };

    /** Slices the filtered problem list to the current page window. */
    const getCurrentPageProblems = () => {
        const filtered = getFilteredProblems();
        const startIndex = (currentPage - 1) * questionsPerPage;
        return filtered.slice(startIndex, startIndex + questionsPerPage);
    };

    /** Computes the total number of pages based on filtered results and page size. */
    const getTotalPages = () => Math.ceil(getFilteredProblems().length / questionsPerPage);

    /** Returns Tailwind text-color class for a given difficulty level. */
    const getDifficultyColor = (difficulty) => {
        switch (difficulty?.toLowerCase()) {
            case 'easy': return 'text-green-600';
            case 'medium': return 'text-yellow-600';
            case 'hard': return 'text-red-600';
            default: return 'text-gray-600';
        }
    };

    /** Navigates to the problem-solving page for a given question. */
    const handleQuestionClick = (questionId) => navigate(`/problems/${questionId}`);

    /**
     * @component CompanyTag
     * @description Attempts to render a company logo SVG from the public folder.
     *              Falls back to a styled text span if the logo is missing.
     */
    const CompanyTag = ({ company, className = "", isGrayscale = false }) => {
        const [hasError, setHasError] = useState(false);
        if (!company) return null;

        const normalizedName = company.toLowerCase().trim().replace(/\s+/g, '_');
        const logoPath = `/company_logos/${normalizedName}.png`;

        if (hasError) {
            return (
                <span className={`text-xs font-semibold ${isGrayscale ? 'text-muted-foreground opacity-50' : 'text-amber-700'} capitalize ${className}`}>
                    {company}
                </span>
            );
        }

        return (
            <div className="flex items-center justify-center h-8 w-auto" title={company.charAt(0).toUpperCase() + company.slice(1)}>
                <img
                    src={logoPath}
                    alt={company}
                    onError={() => setHasError(true)}
                    className={`h-6 w-auto max-w-[70px] object-contain transition-all duration-300 ${isGrayscale ? 'grayscale opacity-70' : 'grayscale-0 opacity-100'}`}
                />
            </div>
        );
    };

    /* ══════════════════════════════════════════════════════════════
     * RENDER HELPERS
     * ══════════════════════════════════════════════════════════════ */

    if (loading) {
        return <DSAProblemsSkeleton navigate={navigate} />;
    }

    if (error) {
        return (
            <div className="min-h-screen bg-gradient-to-r from-emerald-50 via-teal-50 to-emerald-50 text-foreground flex flex-col font-sans">
                <Navbar navigate={navigate} navLinks={navLinks} />
                <div className="flex-1 flex items-center justify-center p-4">
                    <div className="text-center bg-white p-8 rounded-xl shadow-sm border-t-4 border-red-500">
                        <FiAlertCircle className="text-6xl text-red-500 mx-auto mb-4" />
                        <h2 className="text-2xl font-bold text-foreground mb-2">Error Loading Data</h2>
                        <p className="text-muted-foreground mb-6 max-w-md mx-auto">{error}</p>
                        <button onClick={fetchQuestions} className="px-6 py-3 bg-emerald-600 hover:bg-emerald-700 text-white rounded font-medium transition-all duration-200 flex items-center justify-center space-x-2 mx-auto">
                            <FiRefreshCw className="text-lg" />
                            <span>Retry Connection</span>
                        </button>
                    </div>
                </div>
                <Footer navigate={navigate} navLinks={navLinks} />
            </div>
        );
    }

    const totalSolved = questions.filter(q => q.is_solved).length;
    const totalCount = questions.length;
    const completionPct = totalCount > 0 ? Math.round((totalSolved / totalCount) * 100) : 0;

    return (
        <div className="min-h-screen bg-accent text-foreground flex flex-col font-sans">
            <Navbar navigate={navigate} navLinks={navLinks} />

            <main className="flex-1 pt-16">

                {/* ── Dashboard Header ── */}
                <section className="bg-gradient-to-r from-emerald-50 via-teal-50 to-emerald-50 mb-8 mt-4">
                    <div className="max-w-8xl mx-auto px-4 sm:px-12 lg:px-16">
                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                            <h1 className="text-left text-3xl font-bold text-foreground underline decoration-dotted decoration-foreground underline-offset-8">
                                DSA <span className="bg-gradient-to-r from-emerald-600 to-teal-600 bg-clip-text text-transparent underline decoration-dotted decoration-emerald-600 ml-1">Practice Arena</span>
                            </h1>
                            {/* <p className="text-muted-foreground text-sm font-medium">Master Algorithms • Ace Interviews</p> */}
                        </div>
                    </div>
                </section>

                <div className="max-w-8xl mx-auto px-4 sm:px-12 lg:px-16 pb-20">

                    {/* ════════ TOP BENTO GRID: STATS & CHALLENGE ════════ */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">

                        {/* 1. Daily Challenge Box */}
                        {dailyProblem ? (
                            <div className="md:col-span-2 bg-emerald-600 rounded px-4 py-4 text-white flex flex-col justify-between group overflow-hidden relative cursor-pointer"
                                onClick={() => navigate(`/problems/${dailyProblem.question_id}`)}>
                                {/* Deco pattern */}
                                <div className="absolute right-0 top-0 opacity-10 transform translate-x-1/4 -translate-y-1/4 pointer-events-none -rotate-12">
                                    <FiCode className="w-64 h-64" />
                                </div>
                                <div className="relative z-10">
                                    <div className="flex items-center gap-2 mb-3 text-emerald-100">
                                        <FiCalendar className="w-4 h-4 mb-0.5" />
                                        <span className="font-semibold text-md uppercase tracking-wider">Today's Daily Challenge</span>
                                    </div>
                                    <div className="flex items-center justify-between mb-4 pr-4">
                                        <h3 className="text-2xl sm:text-3xl font-bold text-left">{dailyProblem.questions.title}</h3>
                                        {dailyStreak.solvedToday && (
                                            <div className="hidden md:flex w-10 h-10 bg-white text-emerald-700 rounded-full flex items-center justify-center flex-shrink-0" title="Completed Today">
                                                <FiCheck className="w-6 h-6" strokeWidth={3} />
                                            </div>
                                        )}
                                    </div>
                                    <div className="flex flex-wrap items-center gap-3 text-sm font-medium">
                                        <span className="py-1 text-emerald-100 rounded capitalize">{dailyProblem.questions.topic}</span>
                                        <span className='text-emerald-100'>|</span>
                                        <span className={`py-1 rounded text-emerald-100 capitalize`}>{dailyProblem.questions.difficulty}</span>
                                    </div>
                                </div>
                                <div className="relative z-10 mt-2 flex items-center justify-start hidden md:flex">
                                    <span className="flex items-center gap-2 text-emerald-100 font-medium hover:text-white text-left text-md whitespace-nowrap">
                                        {dailyStreak.solvedToday ? (
                                            <>Challenge completed for today! Attempt Again <FiArrowRight className="group-hover:text-white transform transition-transform group-hover:translate-x-1" /></>
                                        ) : (
                                            <>Solve now to maintain your streak <FiArrowRight className="group-hover:text-white transform transition-transform group-hover:translate-x-1" /></>
                                        )}
                                    </span>
                                </div>
                            </div>
                        ) : (
                            <div className="md:col-span-2 bg-white border-l-4 border-emerald-600 rounded p-6 shadow-sm flex items-center justify-center">
                                <div className="text-center text-muted-foreground"><FiActivity className="w-8 h-8 mx-auto mb-2 opacity-50" /><p>Loading Daily Challenge...</p></div>
                            </div>
                        )}

                        {/* 2. Your Progress Box (Only for Logged In Users) */}
                        {isAuthenticated && (
                            <div className="bg-white border-l-4 border-emerald-600 rounded p-4 shadow-sm flex flex-col">
                                <div className="flex items-center gap-2 mb-6 text-center justify-center">
                                    <FiTerminal className="text-emerald-600 w-5 h-5 relative bottom-[0.5px]" />
                                    <h3 className="text-md font-semibold text-foreground tracking-wider mt-0.5">Your Mastery</h3>
                                </div>
                                <div className="grid grid-cols-2 gap-4 flex-1">
                                    <div className="bg-accent rounded p-3 flex flex-col justify-center">
                                        <span className="flex items-center justify-center gap-1.5 text-muted-foreground text-xs font-semibold mb-1 uppercase tracking-widest">
                                            Solved
                                            <FiCheck className="w-4 h-4" />
                                        </span>
                                        <div className="text-3xl font-bold text-foreground mt-1 text-center">
                                            {totalSolved}<span className="text-sm font-medium text-muted-foreground ml-1">/{totalCount}</span>
                                        </div>
                                    </div>
                                    <div className="bg-accent rounded p-3 flex flex-col justify-center">
                                        <span className="flex items-center justify-center gap-1.5 text-muted-foreground text-xs font-semibold mb-1 uppercase tracking-widest">
                                            Streak
                                            <FaFire className="w-4 h-3 mb-1" />
                                        </span>
                                        <div className="text-3xl font-bold text-foreground flex items-center justify-center gap-2 mt-1">
                                            {submissionStreak.currentStreak}
                                        </div>
                                    </div>
                                </div>

                                <div className="mt-4 w-full bg-accent rounded h-1">
                                    <div className="h-1 rounded bg-emerald-600 transition-all duration-700 ease-out" style={{ width: `${completionPct}%` }} />
                                </div>
                            </div>
                        )}

                    </div>

                    {/* ════════ MIDDLE ROW: STICKY COMMAND BAR ════════ */}
                    <div className="sticky top-16 z-30 bg-accent pt-2 pb-6">
                        <div className="bg-white rounded border-l-4 border-emerald-600 p-4 space-y-4">

                            {/* Top row: Search | Company Tags | Difficulties */}
                            <div className="flex flex-col lg:flex-row gap-4 items-center justify-between">
                                {/* Search */}
                                <div className="relative w-full lg:w-80 flex-shrink-0">
                                    <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-emerald-600 w-4 h-4" />
                                    <input
                                        type="text"
                                        placeholder="Search problems, topics, or concepts..."
                                        value={searchQuery}
                                        onChange={(e) => setSearchQuery(e.target.value)}
                                        className="w-full bg-accent text-foreground text-sm rounded-t pl-10 pr-4 py-2.5 outline-none border-t-0 border-x-0 border-b-2 border-transparent focus:outline-none focus:ring-0 focus:border-emerald-600 transition-colors placeholder:text-muted-foreground/70"
                                    />
                                </div>

                                {/* Company Tags Scrollable Ribbon */}
                                {isAuthenticated && <div className="hidden sm:flex flex-1 min-w-0 w-full lg:w-auto md:border-l md:border-border md:pl-4 items-center gap-3">
                                    <div className="relative group flex-1 min-w-0">
                                        {companyTags.length > 0 ? (
                                            <div 
                                                ref={companiesScrollRef} 
                                                onScroll={handleCompaniesScroll} 
                                                className="flex items-center gap-[6px] overflow-x-auto pb-1 lg:pb-0 scrollbar-hide pr-10" 
                                                style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
                                            >
                                                {companyTags.map((company) => (
                                                    <button
                                                        key={company}
                                                        onClick={() => toggleCompany(company)}
                                                        className={`transition-all p-0.5 flex-shrink-0
                                                            ${selectedCompanies.includes(company)
                                                                ? ''
                                                                : ''
                                                            }`}
                                                    >
                                                        <CompanyTag company={company} isGrayscale={!selectedCompanies.includes(company)} />
                                                    </button>
                                                ))}
                                            </div>
                                        ) : (
                                            <div className="text-xs text-muted-foreground italic hidden lg:block">No tags available</div>
                                        )}

                                        {canScrollLeftCompanies && (
                                            <div onClick={scrollCompaniesLeft} className="absolute left-0 top-0 bottom-0 w-20 bg-gradient-to-r from-white from-50% to-transparent flex items-center justify-start pl-1 cursor-pointer pointer-events-auto z-10 transition-all">
                                                <div className="bg-white/50 p-1 rounded-full shadow-sm">
                                                    <FiChevronsLeft className="text-emerald-700 w-5 h-5 hover:scale-110 transition-transform" />
                                                </div>
                                            </div>
                                        )}
                                        {canScrollRightCompanies && (
                                            <div onClick={scrollCompaniesRight} className="absolute right-0 top-0 bottom-0 w-20 bg-gradient-to-l from-white from-50% to-transparent flex items-center justify-end pr-1 cursor-pointer pointer-events-auto z-10 transition-all">
                                                <div className="bg-white/50 p-1 rounded-full shadow-sm">
                                                    <FiChevronsRight className="text-emerald-700 w-5 h-5 hover:scale-110 transition-transform" />
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>}

                                {/* Difficulty Filters */}
                                <div className="flex items-center gap-2 w-full lg:w-auto overflow-x-auto pb-1 lg:pb-0 flex-shrink-0 md:border-l md:border-border/50 md:pl-4">
                                    {['easy', 'medium', 'hard'].map((diff) => (
                                        <button
                                            key={diff}
                                            onClick={() => setSelectedDifficulty(selectedDifficulty === diff ? 'all' : diff)}
                                            className={`px-3 py-1 rounded text-xs font-semibold capitalize transition-all whitespace-nowrap border
                                                ${selectedDifficulty === diff
                                                    ? (diff === 'easy' ? 'bg-emerald-600 text-white border-emerald-600 shadow-sm' :
                                                        diff === 'medium' ? 'bg-yellow-600 text-white border-yellow-600 shadow-sm' :
                                                            'bg-red-600 text-white border-red-600 shadow-sm')
                                                    : 'bg-accent text-muted-foreground hover:bg-emerald-50 hover:text-emerald-700 border-transparent'}`}
                                        >
                                            {diff}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {/* Bottom row: Topics Chips */}
                            <div className="pt-3 border-t border-border/50 relative group">
                                <div ref={topicsScrollRef} onScroll={handleScroll} className="flex items-center gap-2 overflow-x-auto pb-1 scrollbar-hide pr-8" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
                                    {topics.map(t => (
                                        <button
                                            key={t.id}
                                            onClick={() => setSelectedTopic(selectedTopic === t.id ? 'all' : t.id)}
                                            className={`px-3 py-1.5 rounded text-sm font-medium whitespace-nowrap transition-colors border flex items-center gap-1.5
                                                ${selectedTopic === t.id
                                                    ? 'bg-emerald-600 text-white border-emerald-600 shadow-sm'
                                                    : 'bg-accent text-muted-foreground border-transparent hover:bg-emerald-50 hover:text-emerald-700'
                                                }`}
                                        >
                                            {t.name}
                                            <span className={`flex items-center justify-center w-5 h-5 shrink-0 rounded-full text-[10px] font-bold ${selectedTopic === t.id ? 'bg-white text-emerald-600' : 'bg-white text-muted-foreground'}`}>
                                                {t.count}
                                            </span>
                                        </button>
                                    ))}
                                </div>
                                {canScrollLeft && (
                                    <div onClick={scrollTopicsLeft} className="absolute left-0 top-3 bottom-0 w-20 bg-gradient-to-r from-white from-50% to-transparent flex items-center justify-start pl-1 cursor-pointer pointer-events-auto z-10">
                                        <div className="bg-white/50 p-1 transition-colors">
                                            <FiChevronsLeft className="text-emerald-700 w-5 h-5 hover:scale-110 transition-transform" />
                                        </div>
                                    </div>
                                )}
                                {canScrollRight && (
                                    <div onClick={scrollTopicsRight} className="absolute right-0 top-3 bottom-0 w-20 bg-gradient-to-l from-white from-50% to-transparent flex items-center justify-end pr-1 cursor-pointer pointer-events-auto z-10">
                                        <div className="bg-white/50 p-1 transition-colors">
                                            <FiChevronsRight className="text-emerald-700 w-5 h-5 hover:scale-110 transition-transform" />
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* ════════ BOTTOM ROW: PROBLEM LIST ════════ */}
                    <div className="bg-white rounded border-l-4 border-emerald-600 shadow-sm flex flex-col">
                        <div className="divide-y divide-border">
                            {(() => {
                                const currentProblems = getCurrentPageProblems();
                                if (currentProblems.length === 0) {
                                    return (
                                        <div className="text-center py-16">
                                            <FiSearch className="text-4xl text-muted-foreground mx-auto mb-3" />
                                            <h3 className="text-lg font-medium text-muted-foreground mb-1">No Problems Found</h3>
                                            {/* <p className="text-muted-foreground text-sm">Try adjusting your filters or search query.</p> */}
                                        </div>
                                    );
                                }
                                return currentProblems.map((problem) => (
                                    <div
                                        key={problem.id}
                                        onClick={() => handleQuestionClick(problem.id)}
                                        className="group flex flex-col sm:grid sm:grid-cols-12 items-center justify-between gap-1 sm:gap-4 px-4 sm:px-6 py-4 hover:bg-emerald-50/50 transition-colors cursor-pointer relative overflow-hidden"
                                    >
                                        {/* ── MOBILE ONLY LAYOUT ── */}
                                        <div className="flex sm:hidden items-center gap-3 w-full">
                                            <span className="flex-shrink-0">
                                                {problem.is_solved ? <FiCheck className="bg-emerald-600 text-white p-0.5 rounded-full w-5 h-5" /> : <FiCircle className="text-muted-foreground w-5 h-5" />}
                                            </span>
                                            <h3 className="text-base font-semibold text-foreground truncate">
                                                <span className="text-emerald-700 font-bold mr-1.5">#{problem.id}</span>
                                                {problem.title}
                                            </h3>
                                        </div>
                                        <div className="flex sm:hidden items-center justify-between w-full mt-1.5 pl-8">
                                            <div className="flex items-center gap-2 text-xs font-medium text-muted-foreground">
                                                <span className="capitalize">{problem.topic?.toLowerCase()}</span>
                                                <span className="opacity-30">|</span>
                                                <span className={`font-bold capitalize ${getDifficultyColor(problem.difficulty)}`}>{problem.difficulty}</span>
                                            </div>
                                            <FiArrowRight className="text-muted-foreground w-4 h-4 ml-2 flex-shrink-0 opacity-70" />
                                        </div>

                                        {/* ── DESKTOP ONLY LAYOUT (sm: and above) ── */}
                                        {/* Title / ID Col (col-span-4) */}
                                        <div className="hidden sm:flex sm:col-span-4 items-start gap-3 min-w-0 pr-2">
                                            {/* Question ID */}
                                            <span className="text-sm font-bold text-emerald-700 min-w-[40px] mt-0.5 flex-shrink-0">
                                                #{problem.id}
                                            </span>

                                            {/* Container for Title */}
                                            <div className="flex flex-col min-w-0">
                                                <h3 className="text-base font-semibold text-foreground group-hover:text-emerald-700 transition-colors truncate">
                                                    {problem.title}
                                                </h3>
                                            </div>
                                        </div>

                                        {/* Topic Col (col-span-2) */}
                                        <div className="hidden sm:flex sm:col-span-2 flex-wrap items-center gap-2">
                                            {problem.topic && (
                                                <span className="text-xs font-medium text-muted-foreground capitalize">
                                                    {problem.topic.toLowerCase()}
                                                </span>
                                            )}
                                        </div>

                                        {/* Company Col (col-span-3) - NEW */}
                                        <div className="hidden sm:flex sm:col-span-2 items-center gap-2">
                                            {isAuthenticated && problem.company_tag && (
                                                <div className="flex flex-wrap items-center gap-x-3">
                                                    {(() => {
                                                        const tags = problem.company_tag.split(',').map(t => t.trim()).sort();
                                                        const displayTags = tags.slice(0, 3);
                                                        const remaining = tags.length - 3;
                                                        return (
                                                            <>
                                                                {displayTags.map((tag, idx) => (
                                                                    <CompanyTag key={idx} company={tag} />
                                                                ))}
                                                                {remaining > 0 && (
                                                                    <div
                                                                        className="w-7 h-7 rounded-full bg-emerald-600 flex items-center justify-center text-xs text-white"
                                                                        title={tags.slice(3).map(t => t.charAt(0).toUpperCase() + t.slice(1)).join(', ')}
                                                                    >
                                                                        +{remaining}
                                                                    </div>
                                                                )}
                                                            </>
                                                        );
                                                    })()}
                                                </div>
                                            )}
                                        </div>

                                        {/* Difficulty Col (col-span-1) */}
                                        <div className="hidden sm:flex sm:col-span-1 items-center">
                                            <span className={`text-xs font-semibold capitalize ${getDifficultyColor(problem.difficulty)}`}>
                                                {problem.difficulty}
                                            </span>
                                        </div>

                                        {/* Status Col (col-span-1) */}
                                        <div className="hidden sm:flex sm:col-span-1 items-center justify-center">
                                            {problem.is_solved ? (
                                                <div className="w-6 h-6 rounded-full flex items-center justify-center bg-emerald-600 text-white">
                                                    <FiCheck strokeWidth={3} className="w-4 h-4" />
                                                </div>
                                            ) : (
                                                <div className="w-6 h-6 rounded-full flex items-center justify-center">
                                                    <FiCircle className="text-muted-foreground w-6 h-6" />
                                                </div>
                                            )}
                                        </div>

                                        {/* Arrow Action Col (col-span-1) */}
                                        <div className="hidden sm:flex sm:col-span-1 items-center justify-end">
                                            <div className="w-8 h-8 flex items-center justify-center text-muted-foreground transition-colors">
                                                <FiArrowRight className="transform group-hover:translate-x-0.5 transition-transform" />
                                            </div>
                                        </div>
                                    </div>
                                ));
                            })()}
                        </div>

                        {/* Pagination Bottom */}
                        {getTotalPages() > 1 && (
                            <div className="border-t border-border bg-accent/20 px-6 py-4 rounded-b">
                                <PaginationControls
                                    currentPage={currentPage}
                                    setCurrentPage={setCurrentPage}
                                    getTotalPages={getTotalPages}
                                    totalFiltered={getFilteredProblems().length}
                                    questionsPerPage={questionsPerPage}
                                />
                            </div>
                        )}

                    </div>
                </div>
            </main>

            <Footer navigate={navigate} navLinks={footerLinks} />
        </div>
    );
};

/**
 * @component PaginationControls
 * @description Renders a compact prev/next paginator with page position indicator.
 *              Shows the current window range (e.g. "Showing 1-15 of 42 problems").
 *
 * @param {Object} props
 * @param {number} props.currentPage - The currently active page (1-indexed).
 * @param {Function} props.setCurrentPage - State setter for page navigation.
 * @param {Function} props.getTotalPages - Returns the total number of pages.
 * @param {number} props.totalFiltered - Total number of problems after filters.
 * @param {number} props.questionsPerPage - Page size constant.
 *
 * @returns {JSX.Element|null} Pagination bar, or null when only one page exists.
 */
const PaginationControls = ({ currentPage, setCurrentPage, getTotalPages, totalFiltered, questionsPerPage }) => {
    const totalPages = getTotalPages();
    if (totalPages <= 1) return null;

    return (
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div className="text-sm text-muted-foreground font-medium order-2 sm:order-1 text-center sm:text-left">
                Showing <span className="text-foreground">{((currentPage - 1) * questionsPerPage) + 1}-{Math.min(currentPage * questionsPerPage, totalFiltered)}</span> of <span className="text-foreground">{totalFiltered}</span> problems
            </div>
            <div className="flex items-center justify-center sm:justify-end gap-2 order-1 sm:order-2">
                <button
                    onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                    disabled={currentPage === 1}
                    className="flex items-center justify-center w-6 h-6 text-emerald-700 font-bold disabled:opacity-40 disabled:hover:bg-transparent disabled:cursor-not-allowed transition-all"
                >
                    <FiChevronLeft className="w-4 h-4" />
                </button>
                <div className="flex items-center gap-1.5 px-2">
                    <span className="font-semibold text-sm text-foreground">{currentPage}</span>
                    <span className="text-muted-foreground text-xs font-medium px-1">/</span>
                    <span className="text-muted-foreground text-sm font-medium">{totalPages}</span>
                </div>
                <button
                    onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                    disabled={currentPage === totalPages}
                    className="flex items-center justify-center w-6 h-6 text-emerald-700 font-bold disabled:opacity-40 disabled:hover:bg-transparent disabled:cursor-not-allowed transition-all"
                >
                    <FiChevronRight className="w-4 h-4" />
                </button>
            </div>
        </div>
    );
};

/**
 * @component DSAProblemsSkeleton
 * @description Animated loading state that mimics the Bento-grid and filter bar layout.
 */
const DSAProblemsSkeleton = ({ navigate }) => {
    return (
        <div className="min-h-screen bg-accent text-foreground flex flex-col font-sans">
            <Navbar navigate={navigate} navLinks={[]} />
            <main className="flex-1 pt-16">

                {/* ── Dashboard Header Skeleton ── */}
                <section className="bg-gradient-to-r from-emerald-50 via-teal-50 to-emerald-50 mb-8 mt-4">
                    <div className="max-w-8xl mx-auto px-4 sm:px-12 lg:px-16 py-6">
                        <div className="h-10 w-80 bg-emerald-100 rounded animate-pulse" />
                    </div>
                </section>

                <div className="max-w-8xl mx-auto px-4 sm:px-12 lg:px-16 pb-20">
                    {/* ════════ TOP BENTO GRID SKELETON ════════ */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                        {/* 1. Daily Challenge Box Skeleton */}
                        <div className="md:col-span-2 h-64 bg-emerald-600/20 rounded animate-pulse" />

                        {/* 2. Your Progress Box Skeleton */}
                        <div className="h-64 bg-white border-l-4 border-emerald-600 rounded shadow-sm animate-pulse flex flex-col p-6">
                            <div className="h-6 w-32 bg-gray-100 rounded mb-8 mx-auto" />
                            <div className="flex-1 grid grid-cols-2 gap-4">
                                <div className="bg-gray-50 rounded ring-1 ring-gray-100" />
                                <div className="bg-gray-50 rounded ring-1 ring-gray-100" />
                            </div>
                        </div>
                    </div>

                    {/* ════════ STICKY COMMAND BAR SKELETON ════════ */}
                    <div className="sticky top-16 z-30 bg-accent pt-2 pb-6">
                        <div className="h-24 bg-white rounded border-l-4 border-emerald-600 shadow-sm animate-pulse flex items-center px-6 gap-6">
                            <div className="h-10 w-64 bg-gray-50 rounded" />
                            <div className="flex-1 h-8 bg-gray-50 rounded hidden md:block" />
                            <div className="h-8 w-48 bg-gray-50 rounded hidden lg:block" />
                        </div>
                    </div>

                    {/* ════════ PROBLEM LIST SKELETON ════════ */}
                    <div className="bg-white rounded shadow-sm overflow-hidden border border-border/50">
                        {Array.from({ length: 12 }).map((_, i) => (
                            <div key={i} className="border-b border-gray-50 px-6 py-5 space-y-3 animate-pulse">
                                {/* Mobile/Desktop Header Link */}
                                <div className="flex items-center gap-3">
                                    <div className="w-5 h-5 bg-gray-100 rounded-full" />
                                    <div className="h-4 bg-gray-100 rounded w-1/3" />
                                </div>
                                {/* Mobile/Desktop Metadata Row */}
                                <div className="flex items-center justify-between pl-8">
                                    <div className="flex gap-4">
                                        <div className="h-3 bg-gray-50 rounded w-16" />
                                        <div className="h-3 bg-gray-50 rounded w-20" />
                                        <div className="h-3 bg-gray-50 rounded w-12" />
                                    </div>
                                    <div className="w-4 h-4 bg-gray-50 rounded mr-2" />
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </main>
        </div>
    );
};

export default DSAProblems;